import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Truck,
  AlertCircle,
  MapPin,
  Clock,
  CheckCircle2,
  RefreshCw,
  LayoutGrid,
  Users,
  Route,
  Radio,
  Car,
  Map,
  CalendarClock,
  Warehouse,
  BarChart3,
  Package,
  ChevronRight,
  TrendingUp,
  Timer,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { KPICard } from "@/components/admin/shared";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { api } from "@/lib/api";
import { deliveryApi } from "@/lib/admin-api/delivery";
import type {
  Delivery,
  DeliverySlot,
} from "@/lib/admin-api/delivery";
import { useDrivers } from "@/hooks/admin/useDrivers";
import { useDeliverySlots } from "@/hooks/admin/useDeliverySlots";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ZoneRow {
  id: string;
  zone_name: string;
  fee_own_car_fcfa: number;
  fee_own_bike_fcfa: number | null;
  fee_yango_estimated_fcfa: number | null;
  estimated_time_minutes: number;
}

interface RegionGroup {
  id: string;
  name: string;
  code: string;
  active: boolean;
  zones: ZoneRow[];
}

interface SubPageLink {
  readonly path: string;
  readonly icon: typeof Truck;
  readonly title: string;
  readonly description: string;
  readonly count?: number;
  readonly accent: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFcfa(amount: number | null): string {
  if (amount == null) return "—";
  return `${new Intl.NumberFormat("fr-SN").format(amount)} FCFA`;
}

function formatMinutes(minutes: number | null): string {
  if (minutes == null) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h${m > 0 ? ` ${m}min` : ""}` : `${m} min`;
}

function formatHourRange(start: number, end: number): string {
  return `${String(start).padStart(2, "0")}:00 – ${String(end).padStart(2, "0")}:00`;
}

function todayIso(): string {
  return format(new Date(), "yyyy-MM-dd");
}

// ---------------------------------------------------------------------------
// Delivery status badge (uses real DeliveryStatus values)
// ---------------------------------------------------------------------------

const DELIVERY_STATUS_STYLES: Record<string, string> = {
  unassigned: "bg-stone-100 text-stone-600",
  assigned: "bg-blue-100 text-blue-700",
  in_transit: "bg-amber-100 text-amber-800",
  delivered: "bg-emerald-100 text-emerald-700",
};

const DELIVERY_STATUS_LABELS: Record<string, string> = {
  unassigned: "Non assigné",
  assigned: "Assigné",
  in_transit: "En cours",
  delivered: "Livrée",
};

function DeliveryStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        DELIVERY_STATUS_STYLES[status] ?? "bg-stone-100 text-stone-600"
      }`}
    >
      {DELIVERY_STATUS_LABELS[status] ?? status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sub-page grid links (counts are wired to live data at render time)
// ---------------------------------------------------------------------------

function buildSubPages(counts: {
  readonly deliveries: number;
  readonly drivers: number;
  readonly zones: number;
  readonly slots: number;
}): readonly SubPageLink[] {
  return [
    {
      path: "/admin/deliveries",
      icon: LayoutGrid,
      title: "Tableau de livraison",
      description: "Kanban des livraisons du jour",
      count: counts.deliveries,
      accent: "bg-blue-50 text-blue-600",
    },
    {
      path: "/admin/drivers",
      icon: Users,
      title: "Chauffeurs",
      description: "Gestion de la flotte de livreurs",
      count: counts.drivers,
      accent: "bg-emerald-50 text-emerald-600",
    },
    {
      path: "/admin/routes",
      icon: Route,
      title: "Itinéraires",
      description: "Optimisation des parcours",
      accent: "bg-violet-50 text-violet-600",
    },
    {
      path: "/admin/tracking",
      icon: Radio,
      title: "Suivi en direct",
      description: "Livraisons actives, actualisation 30 s",
      accent: "bg-orange-50 text-orange-600",
    },
    {
      path: "/admin/yango",
      icon: Car,
      title: "Yango",
      description: "Intégration dispatch externe",
      accent: "bg-yellow-50 text-yellow-700",
    },
    {
      path: "/admin/delivery-zones",
      icon: Map,
      title: "Zones",
      description: "Zones et tarifs de livraison",
      count: counts.zones,
      accent: "bg-teal-50 text-teal-600",
    },
    {
      path: "/admin/delivery-slots",
      icon: CalendarClock,
      title: "Créneaux",
      description: "Gestion des créneaux horaires",
      count: counts.slots,
      accent: "bg-indigo-50 text-indigo-600",
    },
    {
      path: "/admin/depots",
      icon: Warehouse,
      title: "Dépôts",
      description: "Entrepôts et points de départ",
      accent: "bg-stone-100 text-stone-600",
    },
    {
      path: "/admin/delivery-performance",
      icon: BarChart3,
      title: "Performance",
      description: "KPIs et rapports logistiques",
      accent: "bg-rose-50 text-rose-600",
    },
  ];
}

// ---------------------------------------------------------------------------
// Slot card component
// ---------------------------------------------------------------------------

function SlotCard({ slot }: { slot: DeliverySlot }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Clock className="h-5 w-5 shrink-0 text-stone-400" />
          <div>
            <p className="text-sm font-semibold text-stone-800">{slot.label}</p>
            <p className="text-xs text-stone-500">
              {formatHourRange(slot.start_hour, slot.end_hour)}
            </p>
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
            slot.is_active
              ? "border border-emerald-100 bg-emerald-50 text-emerald-700"
              : "border border-stone-200 bg-stone-50 text-stone-500"
          }`}
        >
          {slot.is_active ? "Actif" : "Inactif"}
        </span>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-stone-500">
        <span>{slot.zone_name}</span>
        <span>
          <span className="font-semibold text-stone-700">{slot.capacity}</span>{" "}
          places
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-page card
// ---------------------------------------------------------------------------

function SubPageCard({ link }: { link: SubPageLink }) {
  const Icon = link.icon;

  return (
    <Link
      to={link.path}
      className="group flex items-start gap-4 rounded-xl border border-stone-200 bg-white p-4 transition-all hover:border-stone-300 hover:shadow-sm"
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${link.accent}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-stone-800 group-hover:text-stone-900">
            {link.title}
          </h3>
          <div className="flex items-center gap-2">
            {link.count !== undefined && (
              <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-600">
                {link.count}
              </span>
            )}
            <ChevronRight className="h-4 w-4 shrink-0 text-stone-300 transition-transform group-hover:translate-x-0.5 group-hover:text-stone-500" />
          </div>
        </div>
        <p className="mt-0.5 text-xs text-stone-500">{link.description}</p>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Zone fee table row
// ---------------------------------------------------------------------------

function ZoneTableRow({ zone }: { zone: ZoneRow }) {
  return (
    <tr className="border-b border-stone-100 last:border-0 transition-colors hover:bg-stone-50">
      <td className="px-4 py-3 text-sm font-medium text-stone-800">{zone.zone_name}</td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-stone-700">
        {formatFcfa(zone.fee_own_car_fcfa)}
      </td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-stone-500">
        {zone.fee_own_bike_fcfa != null ? formatFcfa(zone.fee_own_bike_fcfa) : "—"}
      </td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-stone-500">
        {zone.fee_yango_estimated_fcfa != null
          ? formatFcfa(zone.fee_yango_estimated_fcfa)
          : "—"}
      </td>
      <td className="px-4 py-3 text-right text-sm">
        <span className="inline-flex items-center gap-1 text-stone-500">
          <Clock className="h-3.5 w-3.5" />
          {formatMinutes(zone.estimated_time_minutes)}
        </span>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function LogisticsManagement() {
  const { t } = useTranslation(["logistics", "admin"]);
  useAdminPageTitle("Logistique");
  const [selectedRegionIndex, setSelectedRegionIndex] = useState(0);

  // Zones for the public-facing tariff table
  const {
    data: zonesData,
    isLoading: zonesLoading,
    isError: zonesError,
    refetch: refetchZones,
  } = useQuery({
    queryKey: ["delivery-zones"],
    queryFn: () => api.delivery.zones(),
    staleTime: 5 * 60_000,
  });

  const regions: RegionGroup[] = zonesData?.regions ?? [];
  const activeRegion = regions[selectedRegionIndex] ?? null;

  // Today KPIs — derived from the performance report for the current day.
  const today = todayIso();
  const {
    data: perfData,
    isLoading: perfLoading,
  } = useQuery({
    queryKey: ["admin-delivery-today-summary", today],
    queryFn: () => deliveryApi.getPerformance({ from: today, to: today }),
    staleTime: 60_000,
  });

  // Recent deliveries — today's 5 most recent
  const {
    data: recentData,
    isLoading: recentLoading,
  } = useQuery({
    queryKey: ["admin-delivery-recent", today],
    queryFn: () =>
      deliveryApi.list({ date_from: today, date_to: today, limit: 5 }),
    staleTime: 30_000,
  });

  // Drivers — to compute active driver count
  const { data: driversData } = useDrivers();

  // Slots — today's active slots (filter by the current weekday)
  const { data: slotsData, isLoading: slotsLoading } = useDeliverySlots();

  const summary = perfData?.summary;
  const inProgress = useMemo(() => {
    if (!perfData?.by_status) return 0;
    return perfData.by_status
      .filter((s) => s.status === "assigned" || s.status === "in_transit")
      .reduce((acc, s) => acc + s.count, 0);
  }, [perfData?.by_status]);

  const totalZoneCount = useMemo(
    () =>
      regions.reduce((acc, r) => acc + r.zones.length, 0),
    [regions],
  );

  const activeDriverCount = useMemo(
    () => (driversData?.drivers ?? []).filter((d) => d.is_available).length,
    [driversData?.drivers],
  );

  const todaySlots = useMemo(() => {
    const all = slotsData?.slots ?? [];
    const weekday = new Date().getDay(); // 0=Sun .. 6=Sat
    return all
      .filter((s) => s.is_active && s.days_of_week.includes(weekday))
      .slice(0, 3);
  }, [slotsData?.slots]);

  const recentDeliveries: readonly Delivery[] = recentData?.deliveries ?? [];

  const subPages = useMemo(
    () =>
      buildSubPages({
        deliveries: summary?.total_deliveries ?? 0,
        drivers: activeDriverCount,
        zones: totalZoneCount,
        slots: slotsData?.slots.length ?? 0,
      }),
    [summary?.total_deliveries, activeDriverCount, totalZoneCount, slotsData?.slots.length],
  );

  // ---------------------------------------------------------------------------
  // Loading state (zones drive the initial skeleton)
  // ---------------------------------------------------------------------------
  if (zonesLoading) {
    return (
      <AdminLayout>
        <div className="space-y-6 animate-pulse">
          <div className="h-8 w-64 rounded bg-stone-200" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-24 rounded-lg bg-stone-100" />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="h-20 rounded-xl bg-stone-100" />
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  // ---------------------------------------------------------------------------
  // Main view
  // ---------------------------------------------------------------------------
  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Page header */}
        <div className="flex flex-col gap-1">
          <h1 className="font-display text-2xl font-semibold text-stone-900">
            Logistique
          </h1>
          <p className="text-sm text-stone-500">
            Hub central de gestion des livraisons et de la flotte
          </p>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* KPI cards — wired to real data */}
        {/* ---------------------------------------------------------------- */}
        {perfLoading ? (
          <section className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-24 animate-pulse rounded-lg bg-stone-100"
              />
            ))}
          </section>
        ) : (
          <section className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <KPICard
              label="Livraisons aujourd'hui"
              value={summary?.total_deliveries ?? 0}
              icon={<Package className="h-4 w-4" />}
              format="number"
            />
            <KPICard
              label="En cours"
              value={inProgress}
              icon={<Truck className="h-4 w-4" />}
              format="number"
            />
            <KPICard
              label="Terminées"
              value={summary?.delivered ?? 0}
              icon={<CheckCircle2 className="h-4 w-4" />}
              format="number"
            />
            <KPICard
              label="Taux de réussite"
              value={
                summary && summary.total_deliveries > 0
                  ? Math.round((summary.delivered / summary.total_deliveries) * 100)
                  : 0
              }
              icon={<TrendingUp className="h-4 w-4" />}
              format="percent"
            />
            <KPICard
              label="Temps moyen"
              value={formatMinutes(summary?.avg_delivery_minutes ?? null)}
              icon={<Timer className="h-4 w-4" />}
            />
            <KPICard
              label="Chauffeurs actifs"
              value={activeDriverCount}
              icon={<Users className="h-4 w-4" />}
              format="number"
            />
          </section>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Quick access grid */}
        {/* ---------------------------------------------------------------- */}
        <section className="space-y-4">
          <h2 className="text-base font-semibold text-stone-800">
            Accès rapide
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {subPages.map((link) => (
              <SubPageCard key={link.path} link={link} />
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Recent deliveries mini-table — wired to real data */}
        {/* ---------------------------------------------------------------- */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-stone-800">
              Livraisons récentes
            </h2>
            <Link
              to="/admin/deliveries"
              className="flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-800"
            >
              Tout voir
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
            {recentLoading ? (
              <div className="space-y-2 p-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="h-6 animate-pulse rounded bg-stone-100"
                  />
                ))}
              </div>
            ) : recentDeliveries.length === 0 ? (
              <div className="p-10">
                <EmptyState
                  icon={<Package className="h-6 w-6" />}
                  title="Aucune livraison aujourd'hui"
                  description="Les livraisons de la journée apparaîtront ici."
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-100 bg-stone-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                        Référence
                      </th>
                      <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 sm:table-cell">
                        Client
                      </th>
                      <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 md:table-cell">
                        Zone
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                        Statut
                      </th>
                      <th className="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 lg:table-cell">
                        Chauffeur
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                        Heure
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {recentDeliveries.map((d) => (
                      <tr key={d.id} className="transition-colors hover:bg-stone-50">
                        <td className="px-4 py-3 font-medium text-stone-800">
                          {d.order.public_ref}
                        </td>
                        <td className="hidden px-4 py-3 text-stone-600 sm:table-cell">
                          {d.order.customer_name}
                        </td>
                        <td className="hidden px-4 py-3 text-stone-500 md:table-cell">
                          {d.zone?.name ?? "—"}
                        </td>
                        <td className="px-4 py-3">
                          <DeliveryStatusBadge status={d.status} />
                        </td>
                        <td className="hidden px-4 py-3 text-stone-500 lg:table-cell">
                          {d.driver?.full_name ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-stone-500">
                          {new Date(d.updated_at).toLocaleTimeString("fr-SN", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Delivery slots section — wired to real data */}
        {/* ---------------------------------------------------------------- */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-stone-400" />
            <h2 className="text-base font-semibold text-stone-800">
              Créneaux du jour
            </h2>
          </div>

          {slotsLoading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-24 animate-pulse rounded-xl bg-stone-100"
                />
              ))}
            </div>
          ) : todaySlots.length === 0 ? (
            <EmptyState
              icon={<CalendarClock className="h-6 w-6" />}
              title="Aucun créneau aujourd'hui"
              description="Aucun créneau actif n'est planifié pour ce jour."
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {todaySlots.map((slot) => (
                <SlotCard key={slot.id} slot={slot} />
              ))}
            </div>
          )}
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Zone delivery fees section */}
        {/* ---------------------------------------------------------------- */}
        {regions.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Truck className="h-5 w-5 text-stone-400" />
                <h2 className="text-base font-semibold text-stone-800">
                  Tarifs par zone
                </h2>
              </div>
              <Link
                to="/admin/delivery-zones"
                className="flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-800"
              >
                Modifier les tarifs
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Region tabs */}
            <div className="flex flex-wrap gap-2">
              {regions.map((region, idx) => (
                <button
                  key={region.id}
                  onClick={() => setSelectedRegionIndex(idx)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    selectedRegionIndex === idx
                      ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                >
                  {t(`logistics:zone.zones.${region.code}` as Parameters<typeof t>[0], {
                    defaultValue: region.name,
                  })}
                  {!region.active && (
                    <span className="ml-1.5 text-stone-400">(inactif)</span>
                  )}
                </button>
              ))}
            </div>

            {/* Zone table */}
            {activeRegion && (
              activeRegion.zones.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-stone-200 bg-white p-10 text-center">
                  <MapPin className="h-8 w-8 text-stone-200" />
                  <p className="text-sm text-stone-400">
                    Aucune zone configurée pour cette région
                  </p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-stone-100 bg-stone-50">
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                            Zone / Quartier
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                            Voiture
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                            Moto
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                            Yango
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                            Délai
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeRegion.zones.map((zone) => (
                          <ZoneTableRow key={zone.id} zone={zone} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            )}
          </section>
        )}

        {/* Error state for zones */}
        {zonesError && (
          <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-stone-300 bg-white p-16 text-center">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <div>
              <p className="text-sm font-medium text-stone-700">
                Impossible de charger les zones de livraison
              </p>
              <p className="mt-1 text-xs text-stone-400">
                Vérifiez la connexion au serveur et réessayez.
              </p>
            </div>
            <button
              onClick={() => refetchZones()}
              className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
            >
              <RefreshCw className="h-4 w-4" />
              Réessayer
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
